import { computed, action, observable } from 'mobx';
import { emit, events, on } from 'elc-service-bus';
import { Shade } from '../domain/entities/Shade';
import { IProductShadesSidebarProps } from '../views/product-shades-sidebar/ProductShadesSidebar';
import { ProductBaseController } from './ProductBaseController';
import { ProductRepository } from '../repositories/ProductRepository';
import { diContainer, serviceNames } from '../../service-setup/diContainer';

export interface IShadesSidebarProduct {
    productId: string;
}

export interface IShadesByColorFamily {
    [key: string]: Shade[];
}

export class ProductShadesSidebarViewController extends ProductBaseController {
    @observable protected productRepository: ProductRepository;
    @observable private isProductShadesViewOpen: boolean = false;
    @observable public defaultShade: Shade;
    @observable public isLoaded: boolean = false;
    @observable public isShadeSidebarVtoOpened: boolean = false;

    constructor() {
        super();

        this.productRepository = diContainer.get(serviceNames.productRepository);
    }

    @action
    public initialize = (props: IProductShadesSidebarProps) => {
        const product = this.productRepository.getProductById(
            props.config.selectedSku.partialParentProduct.productId
        );
        if (!!product) {
            this.setProduct(product);
            this.isLoading = false;
            this.isLoaded = true;
        }
    };

    @computed
    get data() {
        return {
            isProductShadesViewOpen: this.isProductShadesViewOpen,
            shades: this.shades,
            isLoaded: this.isLoaded,
            isLoading: this.isLoading,
            shadesByColorFamily: this.shadesByColorFamily,
            selectedSku: this.selectedSku,
            largeImages: this.largeImages,
            imageSliderSettings: this.imageSliderSettings,
            isShadeSidebarVtoOpened: this.isShadeSidebarVtoOpened
        };
    }

    @action
    public onCloseProductShadesView = () => {
        this.isProductShadesViewOpen = false;
        emit(events.SHADE_SIDEBAR_CLOSED);
    };

    @action
    public async onProductShadesViewClick(defaultShade?: Shade) {
        this.isProductShadesViewOpen = true;
        if (defaultShade) {
            this.setDefaultShade(defaultShade);
        }
    }

    @action
    private setDefaultShade(defaultShade: Shade) {
        this.defaultShade = defaultShade;
    }

    @computed
    private get shades(): Shade[] {
        return this.product ? this.product.shades : [];
    }

    @computed
    get shadesByColorFamily() {
        if (this.product) {
            return this.product.skus.reduce((shadesByColorFamily: IShadesByColorFamily, sku) => {
                const { colorFamily, shades } = sku;
                const value = colorFamily.length > 0 ? colorFamily[0].value : '';
                shadesByColorFamily[value] = (shadesByColorFamily[value] || []).concat(
                    Array.from(shades)
                );

                return shadesByColorFamily;
            }, {});
        }
    }

    @computed
    private get imageSliderSettings() {
        return this.imageCarouselSettings && this.imageCarouselSettings.productShadesSidebar;
    }

    @on(events.VTO_OPENED)
    public openShadeSidebarVto = () => {
        this.updateVtoOpenedState(true);
    };

    @on(events.VTO_CLOSED)
    public closeShadeSidebarVto = () => {
        this.updateVtoOpenedState(false);
    };

    @on(events.SHADE_SIDEBAR_OPENED)
    public updateShadeSidebarOpenState = () => {
        this.onProductShadesViewClick();
    };

    @action
    public updateVtoOpenedState = (isOpened: boolean) => {
        this.isShadeSidebarVtoOpened = isOpened;
    };
}
